import { writable } from 'svelte/store'

export interface UpdateInfo {
  version: string
  releaseDate?: string
  releaseNotes?: string
}

export interface DownloadProgress {
  bytesPerSecond: number
  percent: number
  transferred: number
  total: number
}

export interface UpdateState {
  checking: boolean
  updateAvailable: boolean
  updateInfo: UpdateInfo | null
  downloading: boolean
  downloadProgress: DownloadProgress | null
  updateDownloaded: boolean
  error: string | null
  lastChecked: number | null
}

// Initial state
const initialState: UpdateState = {
  checking: false,
  updateAvailable: false,
  updateInfo: null,
  downloading: false,
  downloadProgress: null,
  updateDownloaded: false,
  error: null,
  lastChecked: null
}

// Create the store
export const updateStore = writable<UpdateState>(initialState)

// Actions
export const updateActions = {
  /**
   * Check for updates
   */
  async checkForUpdates(): Promise<void> {
    if (typeof window === 'undefined' || !window.api) {
      console.warn('Update check not available: not in Electron environment')
      return
    }

    updateStore.update(state => ({
      ...state,
      checking: true,
      error: null
    }))

    try {
      const result = await window.api.checkForUpdates() as any

      // If no updates available, reset checking state
      if (result && result.success && !result.updateInfo) {
        updateStore.update(state => ({
          ...state,
          checking: false,
          updateAvailable: false,
          lastChecked: Date.now()
        }))
      } else if (result && !result.success) {
        // Handle error
        updateStore.update(state => ({
          ...state,
          checking: false,
          error: result.error || 'Failed to check for updates',
          lastChecked: Date.now()
        }))
      }
      // If update is available, the 'update:available' event will handle it and set lastChecked
    } catch (error) {
      console.error('Failed to check for updates:', error)
      updateStore.update(state => ({
        ...state,
        checking: false,
        error: error instanceof Error ? error.message : 'Failed to check for updates',
        lastChecked: Date.now()
      }))
    }
  },

  /**
   * Download update
   */
  async downloadUpdate(): Promise<void> {
    if (typeof window === 'undefined' || !window.api) {
      console.warn('Download update not available')
      return
    }

    const downloadFn = (window.api as any).downloadUpdate as (() => Promise<void>) | undefined
    if (!downloadFn) {
      console.warn('Download update function not available')
      return
    }

    updateStore.update(state => ({
      ...state,
      downloading: true,
      error: null
    }))

    try {
      await downloadFn()
    } catch (error) {
      console.error('Failed to download update:', error)
      updateStore.update(state => ({
        ...state,
        downloading: false,
        error: error instanceof Error ? error.message : 'Failed to download update'
      }))
    }
  },

  /**
   * Install update and restart
   */
  async installUpdate(): Promise<void> {
    if (typeof window === 'undefined' || !window.api) {
      console.warn('Install update not available')
      return
    }

    const installFn = (window.api as any).installUpdate as (() => Promise<void>) | undefined
    if (!installFn) {
      console.warn('Install update function not available')
      return
    }

    try {
      await installFn()
      // App will quit and restart
    } catch (error) {
      console.error('Failed to install update:', error)
      updateStore.update(state => ({
        ...state,
        error: error instanceof Error ? error.message : 'Failed to install update'
      }))
    }
  },

  /**
   * Handle update available event
   */
  handleUpdateAvailable(info: UpdateInfo): void {
    updateStore.update(state => ({
      ...state,
      checking: false,
      updateAvailable: true,
      updateInfo: info,
      lastChecked: Date.now()
    }))
  },

  /**
   * Handle update progress
   */
  handleUpdateProgress(progress: DownloadProgress): void {
    updateStore.update(state => ({
      ...state,
      downloadProgress: progress
    }))
  },

  /**
   * Handle update downloaded
   */
  handleUpdateDownloaded(info: UpdateInfo): void {
    updateStore.update(state => ({
      ...state,
      downloading: false,
      downloadProgress: null,
      updateDownloaded: true,
      updateInfo: info
    }))
  },

  /**
   * Clear error
   */
  clearError(): void {
    updateStore.update(state => ({
      ...state,
      error: null
    }))
  },

  /**
   * Reset state
   */
  reset(): void {
    updateStore.set(initialState)
  }
}

// Setup event listeners if in Electron environment
if (typeof window !== 'undefined' && window.api) {
  const api = window.api as any

  // Listen for update available
  const onUpdateAvailableFn = api.onUpdateAvailable as ((callback: (info: UpdateInfo) => void) => void) | undefined
  if (onUpdateAvailableFn) {
    onUpdateAvailableFn((info: UpdateInfo) => {
      updateActions.handleUpdateAvailable(info)
    })
  }

  // Listen for update not available
  const onUpdateNotAvailableFn = api.onUpdateNotAvailable as ((callback: (info: UpdateInfo) => void) => void) | undefined
  if (onUpdateNotAvailableFn) {
    onUpdateNotAvailableFn((info: UpdateInfo) => {
      // Reset checking state when no update is available
      updateStore.update(state => ({
        ...state,
        checking: false,
        updateAvailable: false,
        lastChecked: Date.now()
      }))
    })
  }

  // Listen for update error
  const onUpdateErrorFn = api.onUpdateError as ((callback: (error: { message: string; stack?: string }) => void) => void) | undefined
  if (onUpdateErrorFn) {
    onUpdateErrorFn((error: { message: string; stack?: string }) => {
      // Reset states and show error
      updateStore.update(state => ({
        ...state,
        checking: false,
        downloading: false,
        downloadProgress: null,
        error: error.message
      }))
    })
  }

  // Listen for download progress
  const onUpdateProgressFn = api.onUpdateProgress as ((callback: (progress: DownloadProgress) => void) => void) | undefined
  if (onUpdateProgressFn) {
    onUpdateProgressFn((progress: DownloadProgress) => {
      updateActions.handleUpdateProgress(progress)
    })
  }

  // Listen for update downloaded
  const onUpdateDownloadedFn = api.onUpdateDownloaded as ((callback: (info: UpdateInfo) => void) => void) | undefined
  if (onUpdateDownloadedFn) {
    onUpdateDownloadedFn((info: UpdateInfo) => {
      updateActions.handleUpdateDownloaded(info)
    })
  }
}
